import './EsWelcome.css';

import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import {
    API_CAMPAIGN_LIST, API_CHARACTER_LIST, VIEW_CAMPAIGNS, VIEW_CHARACTER_NEW, VIEW_CHARACTERS, VIEW_CHAT, VIEW_CODEX,
    VIEW_DEMO
} from '../../common/EsConstants';
import { useAuth } from '../parent/EsAuth';
import EsBirthdayBanner from './EsBirthdayBanner';
import {
    useEsCharactersFlag, useEsChatFlag, useEsCodexFlag, useEsDashboardFlag, useEsDemoModeFlag
} from '../../common/EsFlags';
import { esFetch } from '../../common/EsFetch';
import { useEsStrings } from '../../common/EsStrings';
import { useEsMaker } from '../../common/EsSubs';
import EsMinis from '../../common/EsMinis';

/**
 * Where a sign-in lands (EsLogin, EsRegister, EsBirthdate): a numbered path of next steps,
 * each one sentence and one link to the page that does it.
 *
 * A step shows only when the gate on its route lets the reader through, so the page never
 * links to a redirect. The demo table is behind two: switchEsDemoMode and the campaigns'
 * EsDashboardGate around /view/demo.
 *
 * A step is checked once its button is pressed, never from what the account holds (Tim,
 * 2026-10-31), and remembered per account in this browser only. Each button opens a new tab,
 * so the path stays open behind it. characterList and campaignList still pick the character
 * and campaign buttons' words, each read only when its flag is on, since esback answers 404
 * behind a closed one.
 */

const DONE_KEY = 'esWelcomeDone.';
const STEP_ORDER = [ 'codex', 'commons', 'character', 'campaign', 'demo' ];

// The steps this account has pressed, from localStorage; empty when storage is closed.
const readDone = ( pAccountId ) => {
    try {
        const aList = JSON.parse( window.localStorage.getItem( DONE_KEY + pAccountId ) || '[]' );
        return Array.isArray( aList ) ? aList : [];
    } catch {
        return [];
    }
};

const writeDone = ( pAccountId, pList ) => {
    try {
        window.localStorage.setItem( DONE_KEY + pAccountId, JSON.stringify( pList ) );
    } catch {
        // A private window or blocked storage: the check lasts for this visit only.
    }
};

// A list's length, or null when it could not be read.
const countOf = ( pApi ) => esFetch( pApi )
    .then( ( pResponse ) => ( pResponse.ok ? pResponse.json() : null ) )
    .then( ( pList ) => ( Array.isArray( pList ) ? pList.length : null ) )
    .catch( () => null );

// Whether the reader has one: true, false, or null while unread or unreadable.
const useHasAny = ( pOn, pApi ) => {
    const [ count, setCount ] = useState( null );
    useEffect( () => {
        if ( !pOn ) return undefined;
        let aLive = true;
        void countOf( pApi ).then( ( pCount ) => { if ( aLive ) setCount( pCount ); } );
        return () => { aLive = false; };
    }, [ pOn, pApi ] );
    return pOn && count !== null ? count > 0 : null;
};

export default function EsWelcome() {
    const t = useEsStrings();
    const { account } = useAuth();
    const maker = useEsMaker();

    const { ready, on: consoleOn } = useEsDashboardFlag();
    const demoOn = useEsDemoModeFlag();
    const charactersOn = useEsCharactersFlag();
    const codexOn = useEsCodexFlag();
    const chatOn = useEsChatFlag();

    const hasCharacter = useHasAny( charactersOn, API_CHARACTER_LIST );
    const hasCampaign = useHasAny( consoleOn, API_CAMPAIGN_LIST );

    const accountId = account?.id ?? 'anon';
    const [ pressed, setPressed ] = useState( () => readDone( accountId ) );
    useEffect( () => { setPressed( readDone( accountId ) ); }, [ accountId ] );
    const press = ( pKey ) => {
        if ( pressed.includes( pKey ) ) return;
        const aList = [ ...pressed, pKey ];
        setPressed( aList );
        writeDone( accountId, aList );
    };

    // Nothing until the flags have answered: each reads its code default while the client
    // identifies, and the steps would grow under the reader.
    if ( !ready ) return null;

    // Shown in STEP_ORDER, Tim's (2026-10-31): the rules, the people, someone to play, a table
    // of your own, then a game to watch.
    const aSteps = [
        { on: demoOn && consoleOn, key: 'demo', to: VIEW_DEMO,
            text: t( 'welcome-step-demo', 'Play at the Demo table, which resets every hour.' ),
            button: t( 'welcome-step-demo-button', 'Try the Demo' ) },
        { on: charactersOn, key: 'character',
            to: hasCharacter ? VIEW_CHARACTERS : VIEW_CHARACTER_NEW,
            text: t( 'welcome-step-character', 'Build a Character, one choice at a time.' ),
            button: hasCharacter
                ? t( 'welcome-step-character-done-button', 'Your Characters' )
                : t( 'welcome-step-character-button', 'Make a Character' ) },
        { on: consoleOn, key: 'campaign', to: VIEW_CAMPAIGNS,
            text: maker
                ? t( 'welcome-step-campaign', 'Run your own Campaign, or join one with a code.' )
                : t( 'welcome-step-campaign-join', 'Join a Campaign with the code its GM sent you.' ),
            button: hasCampaign
                ? t( 'welcome-step-campaign-done-button', 'Your Campaigns' )
                : t( 'welcome-step-campaign-button', 'Go to Campaigns' ) },
        { on: codexOn, key: 'codex', to: VIEW_CODEX,
            text: t( 'welcome-step-codex', 'Look up a rule or a monster in the Codex.' ),
            button: t( 'welcome-step-codex-button', 'Open the Codex' ) },
        { on: chatOn, key: 'commons', to: VIEW_CHAT,
            text: t( 'welcome-step-commons', 'Say hi in Commons, a forum for everyone.' ),
            button: t( 'welcome-step-commons-button', 'Open the Commons' ) },
    ].filter( ( pStep ) => pStep.on )
        .sort( ( pA, pB ) => STEP_ORDER.indexOf( pA.key ) - STEP_ORDER.indexOf( pB.key ) )
        .map( ( pStep ) => ( { ...pStep, done: pressed.includes( pStep.key ) } ) );

    // The first step not yet done takes the filled button: that is the one to press next.
    const aNext = aSteps.find( ( pStep ) => !pStep.done )?.key;

    return (
        <section data-testid="es-welcome-page" className="welcomeView relative z-10 pb-4 pt-nav-room">
            <EsMinis />
            <div className="container">
                <div className="flex flex-wrap mx-[-16px]">
                    <div className="w-full px-4">
                        <div className="mx-auto max-w-[760px]">

                            <h1 data-testid="es-welcome-heading" className="sectionTitle welcomeHeading">
                                { account?.nickname
                                    ? t( 'welcome-heading-named', 'Welcome, {nickname}', { nickname: account.nickname } )
                                    : t( 'welcome-heading', 'Welcome' ) }
                            </h1>

                            <EsBirthdayBanner birthday={ account?.birthday } name={ account?.nickname } />

                            { aSteps.length > 0
                                ? <ol data-testid="es-welcome-steps" className="welcomeSteps">
                                        { aSteps.map( ( pStep, pIndex ) => (
                                            <li key={ pStep.key } data-testid={ 'es-welcome-step-' + pStep.key }
                                                className="welcomeStep">
                                                <span aria-hidden="true"
                                                      className={ 'welcomeStepNumber' + ( pStep.done ? ' welcomeStepNumberDone' : '' ) }>
                                                    { pStep.done ? <i className="pi pi-check" /> : pIndex + 1 }
                                                </span>
                                                <span className="welcomeStepText">{ pStep.text }</span>
                                                <Link to={ pStep.to } target="_blank" rel="noopener"
                                                      onClick={ () => press( pStep.key ) }
                                                      data-testid={ 'es-welcome-step-' + pStep.key + '-button' }
                                                      className={ ( pStep.key === aNext ? 'buttonPrimary' : 'buttonSecondary' ) + ' welcomeStepButton' }>
                                                    { pStep.button }
                                                </Link>
                                            </li>
                                        ) ) }
                                    </ol>
                                : <p data-testid="es-welcome-none" className="welcomeLead">
                                    { t( 'welcome-start-none', 'Nothing is open to your account yet. We will email you when it is.' ) }
                                  </p> }

                        </div>
                    </div>
                </div>
            </div>
        </section>
    );
}
